'use client'

import { createGlobalStyle } from 'styled-components'
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import styled from 'styled-components'
import studentRequest from 'utils/studentRequest'
import { getUserMediaWithSelectedDevices } from 'utils/mediaDevices'
import { mountHiddenProctorVideo, unmountProctorVideo, watchProctorTrack } from 'utils/proctorVideo'
import { shouldEnforceFullscreen, markMobileInterruption, isMobileInterruptionFullscreenExit } from 'utils/deviceTier'
import {
  saveQuestionResponse,
  submitAssessment,
  resetResponses,
} from '../../actions'
import selectors from '../../selectors'
import Completion from './completion'
import PluginLiveLogo from 'components/icons/PluginLiveLogo'
import ReactMarkdown from 'react-markdown'
import remarkMath from 'remark-math'
import rehypeKatex from 'rehype-katex'
import {
  trackAssessmentAutoSubmitted,
  trackAssessmentCompleted,
  trackFullscreenEntered,
  trackFullscreenExited,
  trackQuestionAnswered,
  trackRightClick,
  trackViolationDetected,
} from '../../../../utils/assessmentEvents'

// Shuffle array using Fisher-Yates algorithm
function shuffleArray(array) {
  const shuffled = [...array]
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
  }
  return shuffled
}

// Constants
const MAX_TAB_SWITCH_WARNINGS = 4

const MarkdownGlobalStyle = createGlobalStyle`
  .markdown-body, .markdown-body * {
    color: #374151 !important;
  }
`

// Fullscreen Assessment Container
const FullscreenAssessmentContainer = styled.div`
  position: ${props => (props.isFullscreen ? 'fixed' : 'relative')};
  top: ${props => (props.isFullscreen ? '0' : 'auto')};
  left: ${props => (props.isFullscreen ? '0' : 'auto')};
  width: ${props => (props.isFullscreen ? '100vw' : '100%')};
  height: ${props => (props.isFullscreen ? '100vh' : 'auto')};
  min-height: ${props => (props.isFullscreen ? '100vh' : 'auto')};
  z-index: ${props => (props.isFullscreen ? '99999' : 'auto')};
  display: flex;
  background: #f9fafb;
  font-family: 'Inter', sans-serif;
  overflow: hidden;
`

const SidebarNav = styled.div`
  width: ${props => (props.isExpanded ? '25%' : '60px')};
  min-width: ${props => (props.isExpanded ? '300px' : '60px')};
  max-width: ${props => (props.isExpanded ? '25%' : '60px')};
  height: 100vh;
  background: #ffffff;
  border-right: 1px solid #e5e7eb;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 100;
  overflow: hidden;
`

const SidebarHeader = styled.div`
  padding: ${props => (props.isExpanded ? '20px 16px' : '20px 0')};
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: ${props => (props.isExpanded ? 'space-between' : 'center')};
  background: #f8fafc;
  min-height: 64px;
  flex-shrink: 0;
`

const SidebarTitle = styled.h3`
  font-size: 15px;
  font-weight: 600;
  margin: 0;
  color: #374151;
  transition: all 0.3s ease;
  white-space: nowrap;
  overflow: hidden;
  display: ${props => (props.isExpanded ? 'block' : 'none')};
`

const ToggleButton = styled.button`
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 8px;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;

  &:hover {
    background: #f3f4f6;
    border-color: #9ca3af;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`

const SidebarContent = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: ${props => (props.isExpanded ? '16px 12px' : '16px 6px')};

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 2px;
  }
`

const SectionTitle = styled.h4`
  font-size: 10px;
  font-weight: 700;
  color: #3b82f6;
  margin: 12px 6px 6px 6px;
  padding: 4px 0;
  border-bottom: 1px solid #e5e7eb;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  opacity: ${props => (props.isExpanded ? 1 : 0)};
  visibility: ${props => (props.isExpanded ? 'visible' : 'hidden')};
  transition: all 0.3s ease;
`

const QuestionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const QuestionItem = styled.div`
  display: flex;
  align-items: center;
  padding: ${props => (props.isExpanded ? '10px 8px' : '12px 6px')};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  min-height: ${props => (props.isExpanded ? '40px' : '48px')};
  position: relative;
  margin-bottom: 2px;

  &:hover {
    background: #f8fafc;
    border-color: #d1d5db;
    transform: translateX(2px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }

  ${props =>
    !props.isExpanded &&
    `
    justify-content: center;
  `}
`

const QuestionNumber = styled.div`
  width: ${props => (props.isExpanded ? '28px' : '36px')};
  height: ${props => (props.isExpanded ? '28px' : '36px')};
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: ${props => (props.isExpanded ? '12px' : '13px')};
  flex-shrink: 0;
  position: relative;
  transition: all 0.2s ease;

  ${props => {
    switch (props.status) {
      case 'current':
        return `
          background: #3b82f6;
          color: white;
          box-shadow: 0 0 0 3px #eff6ff;
          border: 2px solid #3b82f6;
        `
      case 'attempted':
        return `
          background: #10b981;
          color: white;
          border: 2px solid #10b981;
        `
      case 'skipped':
        return `
          background: #6b7280;
          color: white;
          border: 2px solid #6b7280;
        `
      default:
        return `
          background: #f3f4f6;
          color: #6b7280;
          border: 2px solid #e5e7eb;
        `
    }
  }}
`

const QuestionText = styled.div`
  margin-left: ${props => (props.isExpanded ? '8px' : '0')};
  font-size: 12px;
  color: #4b5563;
  font-weight: 500;
  opacity: ${props => (props.isExpanded ? 1 : 0)};
  visibility: ${props => (props.isExpanded ? 'visible' : 'hidden')};
  transition: all 0.3s ease;
  flex: 1;
  line-height: 1.3;
  display: ${props => (props.isExpanded ? '-webkit-box' : 'none')};
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  
  & .markdown-body > * {
    margin: 0 !important;
  }
`

// Hover Popover for collapsed sidebar
const QuestionPopover = styled.div`
  position: fixed;
  left: 70px;
  top: ${props => props.top}px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  max-width: 300px;
  pointer-events: none;
  opacity: ${props => (props.show ? 1 : 0)};
  transform: ${props => (props.show ? 'translateX(0)' : 'translateX(-10px)')};
  transition: all 0.2s ease;

  .popover-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
    padding-bottom: 8px;
    border-bottom: 1px solid #e5e7eb;
  }

  .popover-question-number {
    font-size: 12px;
    font-weight: 600;
    color: #374151;
  }

  .popover-status {
    font-size: 11px;
    padding: 2px 8px;
    border-radius: 4px;
    color: white;
  }

  .popover-content {
    font-size: 13px;
    color: #4b5563;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
`

// Modal Components for Fullscreen and Violations
const FullscreenModalOverlay = styled.div`
  background-color: rgba(0, 0, 0, 0.85);
  z-index: 9999;
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(4px);
`

const FullscreenModalContent = styled.div`
  max-width: 500px;
  width: 90%;
  padding: 30px;
  text-align: center;
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  position: relative;
  margin: 20px;
  max-height: 90vh;
  overflow-y: auto;
`

const FullscreenTitle = styled.h2`
  color: #4318ff;
  margin-bottom: 20px;
  font-size: 24px;
  font-weight: 600;
  line-height: 1.3;
`

const FullscreenDescription = styled.p`
  margin-bottom: 20px;
  line-height: 1.5;
  color: #374151;
  font-size: 14px;

  strong {
    font-weight: 600;
    color: #111827;
  }
`

const PrimaryButton = styled.button`
  background-color: #4318ff;
  color: white;
  font-weight: bold;
  height: 44px;
  border-radius: 4px;
  border: none;
  padding: 0 24px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 180px;

  &:hover {
    background-color: #3512cc;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(67, 24, 255, 0.3);
  }

  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
    transform: none;
  }
`

const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
`

const TopBar = styled.div`
  background: #ffffff;
  padding: 16px 24px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
  height: 64px;
  box-sizing: border-box;
`

const AssessmentTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  display: flex;
  align-items: center;
  gap: 12px;
`

const TimerDisplay = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: ${props => (props.isWarning ? '#dc2626' : '#374151')};
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: ${props => (props.isWarning ? '#fef2f2' : '#f9fafb')};
  border-radius: 8px;
  border: 1px solid ${props => (props.isWarning ? '#fecaca' : '#e5e7eb')};
`

const QuestionArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  background: #ffffff;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f3f4f6;
  }

  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 3px;
  }
`

const QuestionContent = styled.div`
  max-width: 900px;
  margin: 0 auto;
`

const QuestionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid #e5e7eb;
`

const QuestionTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
`

const QuestionMeta = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`

const MetaBadge = styled.span`
  padding: 4px 12px;
  background: #f3f4f6;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
`

const QuestionTextArea = styled.div`
  font-size: 16px;
  line-height: 1.7;
  color: #374151;
  margin-bottom: 24px;
  
  img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    margin: 16px 0;
  }
`

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 24px;
`

const OptionItem = styled.div`
  padding: 16px;
  border: 2px solid ${props => (props.isSelected ? '#3b82f6' : '#e5e7eb')};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => (props.isSelected ? '#eff6ff' : '#ffffff')};

  &:hover {
    border-color: ${props => (props.isSelected ? '#3b82f6' : '#d1d5db')};
    background: ${props => (props.isSelected ? '#eff6ff' : '#f9fafb')};
  }
  
  img {
    max-width: 200px;
    height: auto;
    border-radius: 4px;
    margin-top: 8px;
  }
`

const OptionText = styled.div`
  font-size: 15px;
  color: #374151;
  font-weight: ${props => (props.isSelected ? '500' : '400')};
`

const BottomBar = styled.div`
  background: #ffffff;
  padding: 16px 24px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
  height: 72px;
  box-sizing: border-box;
`

const NavButton = styled.button`
  padding: 10px 20px;
  background: ${props => (props.primary ? '#3b82f6' : '#ffffff')};
  color: ${props => (props.primary ? '#ffffff' : '#374151')};
  border: ${props => (props.primary ? 'none' : '1px solid #d1d5db')};
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: ${props => (props.primary ? '#2563eb' : '#f9fafb')};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`
const InfoContainer = styled.div`
  background-color: #f9fafb;
  padding: 16px 20px;
  border-radius: 8px;
  margin-bottom: 30px;
  display: flex;
  align-items: flex-start;
`

const InfoIcon = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 24px;
  height: 24px;
  background-color: #4318ff;
  color: white;
  border-radius: 50%;
  font-size: 14px;
  font-weight: bold;
  margin-right: 15px;
  flex-shrink: 0;
`

const InfoText = styled.p`
  font-weight: 400;
  font-style: italic;
  font-size: 15px;
  line-height: 109%;
  letter-spacing: 0%;
  color: #111827;
  margin: 0;

  strong {
    font-weight: 600;
    font-style: italic;
    line-height: 100%;
    letter-spacing: 0%;
  }
`

const SubmitButton = styled.button`
  padding: 10px 24px;
  background: #10b981;
  color: #ffffff;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #059669;
  }
`

// Fullscreen Entry Modal Component
const FullscreenEntryModal = ({ onAccept }) => {
  return (
    <FullscreenModalOverlay>
      <FullscreenModalContent onClick={e => e.stopPropagation()}>
        <FullscreenTitle>Enable Full-Page Assessment Mode</FullscreenTitle>
        <FullscreenDescription>
          This assessment will open in full-page mode for optimal focus and
          proctoring purposes. The entire screen will be dedicated to the
          assessment.
        </FullscreenDescription>

        <FullscreenDescription>
          <div style={{ textAlign: 'left', width: '100%' }}>
            <span style={{ marginLeft: '27px' }}>
              <strong>Important:</strong> During this assessment:
            </span>
            <ul style={{ margin: '10px 0' }}>
              <li>The assessment will take over the entire screen</li>
              <li>Navigation elements will be hidden</li>
              <li>Switching tabs is not allowed (3 warnings; the fourth violation auto-submits)</li>
              <li>Browser navigation is disabled</li>
              <li>Key combinations like Alt+Tab are blocked</li>
            </ul>
            Violations may result in automatic submission of your assessment.
          </div>
        </FullscreenDescription>

        <PrimaryButton onClick={onAccept}>
          Accept & Start Assessment
        </PrimaryButton>
      </FullscreenModalContent>
    </FullscreenModalOverlay>
  )
}

// Fullscreen Re-entry Modal Component
const FullscreenReentryModal = ({ onReturn, warningCount, lite = false }) => {
  return (
    <FullscreenModalOverlay>
      <FullscreenModalContent onClick={e => e.stopPropagation()}>
        <FullscreenTitle>Return to Assessment</FullscreenTitle>
        <FullscreenDescription>
          {lite
            ? 'You left the assessment. For security and proctoring purposes, please stay on this screen until you finish.'
            : 'You have exited the assessment mode. For security and proctoring purposes, you must return to full-page assessment mode to continue.'}
        </FullscreenDescription>

        <InfoContainer>
          <InfoIcon>!</InfoIcon>
          <InfoText>
            <strong>Warning {warningCount}/3:</strong> A fourth violation will result in automatic submission of your assessment
            and will be flagged as suspicious.
          </InfoText>
        </InfoContainer>

        <PrimaryButton onClick={onReturn}>
          {lite ? 'Resume Assessment' : 'Return to Assessments'}
        </PrimaryButton>
      </FullscreenModalContent>
    </FullscreenModalOverlay>
  )
}

// Violation Modal Component
const ViolationModal = ({ onSubmit }) => (
  <FullscreenModalOverlay>
    <FullscreenModalContent onClick={e => e.stopPropagation()}>
      <FullscreenTitle>Assessment will be auto-submitted</FullscreenTitle>
      <FullscreenDescription>
        You&apos;ve violated the rules more than 3 times, so your assessment will be auto-submitted.
      </FullscreenDescription>

      <PrimaryButton onClick={onSubmit}>
        <span style={{ fontWeight: 'bold' }}>Understood</span>
      </PrimaryButton>
    </FullscreenModalContent>
  </FullscreenModalOverlay>
)

const CustomAssessment = ({ assessment, questions, studentId, setAssessment }) => {
  const [isFullscreen, setIsFullscreen] = useState(false)
  const dispatch = useDispatch()  // ✅ ADD THIS LINE

  const [sidebarExpanded, setSidebarExpanded] = useState(true)
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0)
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [responses, setResponses] = useState({})
  const [selectedAnswer, setSelectedAnswer] = useState(null)
  const [timeLeft, setTimeLeft] = useState(0)
  const [showSubmitModal, setShowSubmitModal] = useState(false)
  const [showCompletion, setShowCompletion] = useState(false)
  const totalTimeTakenRef = useRef(0)
  const [totalTimeTakenDisplay, setTotalTimeTakenDisplay] = useState(0)
  const globalTimerInitialized = useRef(false)
  const [tabSwitchCount, setTabSwitchCount] = useState(0)
  const currentAssessment = useSelector(selectors.getCurrentAssessment)
  const proctoringEnabled = currentAssessment?.allowProctoring !== false
  // Fullscreen lockdown is only enforceable on devices that actually support
  // element fullscreen (desktop / macOS Safari / iPad). iPhone Safari cannot
  // fullscreen a web element, so there we run proctoring in "lite" mode:
  // snapshots + tab/app-switch detection stay ON, but the fullscreen entry gate,
  // re-entry-to-fullscreen and fullscreen-exit violations are skipped.
  const enforceFullscreen = proctoringEnabled && shouldEnforceFullscreen()
  const [showFullscreenModal, setShowFullscreenModal] = useState(enforceFullscreen)  // Only show modal when fullscreen is enforced
  const [showReentryModal, setShowReentryModal] = useState(false)
  const [showViolationModal, setShowViolationModal] = useState(false)
  const [violationType, setViolationType] = useState('')
  const [hoveredQuestion, setHoveredQuestion] = useState(null)
  const [popoverPosition, setPopoverPosition] = useState({ top: 0 })
  const [isAssessmentActive, setIsAssessmentActive] = useState(!enforceFullscreen)  // Auto-activate when there is no fullscreen gate (proctoring off, or lite mode)
  const [isAssessmentFullscreen, setIsAssessmentFullscreen] = useState(false)
  const assessmentActiveRef = useRef(isAssessmentActive)
  const completedRef = useRef(showCompletion)
  const handleSubmitRef = useRef(null)
  const isAutoSubmittedRef = useRef(false)
  const violationSubmissionStartedRef = useRef(false)
  const violationSubmitTimerRef = useRef(null)
  const submitAfterViolation = () => {
    if (violationSubmissionStartedRef.current) return
    violationSubmissionStartedRef.current = true
    if (violationSubmitTimerRef.current) clearTimeout(violationSubmitTimerRef.current)
    isAutoSubmittedRef.current = true
    handleSubmitRef.current()
  }

  // Proctoring states
  const [captureCounter, setCaptureCounter] = useState(0)
  const captureRef = useRef(null)
  const canvasRef = useRef(null)
  const captureInterval = useRef(null)
  const proctoringActive = useRef(false)
  const capturingInProgress = useRef(false)

  const studentData = useSelector(state => {
    return state.studentData?.studentDetails || state.auth?.user || null
  })
  const timerRef = useRef(null)
  const questionStartTimeRef = useRef(Date.now())
  const hoverTimeoutRef = useRef(null)
  const questionItemRefs = useRef([])

  // Extract sections from questions and shuffle question order per student
  const sections = useMemo(() => {
    const raw = questions?.sections || []
    return raw.map(section => ({
      ...section,
      questions: shuffleArray(section.questions || []),
    }))
  }, [questions])

  const currentSection = sections[currentSectionIndex]
  const currentQuestion = currentSection?.questions?.[currentQuestionIndex]

  // Flatten all questions for easier navigation
  const allQuestions = useMemo(() => {
    return sections.flatMap((section, sIdx) =>
      section.questions.map((q, qIdx) => ({
        ...q,
        sectionIndex: sIdx,
        questionIndex: qIdx,
        sectionName: section.section_name,
        globalIndex: sections.slice(0, sIdx).reduce((sum, s) => sum + s.questions.length, 0) + qIdx
      }))
    )
  }, [sections])
  const blobToBase64 = blob => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => {
        const base64String = reader.result.split(',')[1]
        resolve(base64String)
      }
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
  }

  const captureImage = async () => {
    if (capturingInProgress.current) return null
    capturingInProgress.current = true

    try {
      const safetyClearTimeout = setTimeout(() => {
        if (capturingInProgress.current) capturingInProgress.current = false
      }, 20000)

      if (!captureRef.current) {
        try {
          const proctorStream = await getUserMediaWithSelectedDevices({
            video: { width: 640, height: 480 },
            audio: false,
          })
          captureRef.current = document.createElement('video')
          mountHiddenProctorVideo(captureRef.current)
          watchProctorTrack(proctorStream, () => {
            unmountProctorVideo(captureRef.current)
            captureRef.current = null
            window.proctorStreamTracks = []
          })
          captureRef.current.srcObject = proctorStream
          captureRef.current.autoplay = true
          captureRef.current.muted = true
          await new Promise(resolve => {
            captureRef.current.onloadedmetadata = () => {
              captureRef.current.play().then(resolve).catch(resolve)
            }
            setTimeout(resolve, 1000)
          })
          if (!canvasRef.current) {
            canvasRef.current = document.createElement('canvas')
            canvasRef.current.width = 640
            canvasRef.current.height = 480
          }
          if (!window.proctorStreamTracks) window.proctorStreamTracks = []
          proctorStream.getTracks().forEach(track => {
            window.proctorStreamTracks.push(track)
          })
          proctoringActive.current = true
        } catch (err) {
          console.error('Failed to create proctoring stream:', err)
          clearTimeout(safetyClearTimeout)
          capturingInProgress.current = false
          return null
        }
      }

      try {
        const context = canvasRef.current.getContext('2d')
        context.drawImage(
          captureRef.current,
          0,
          0,
          canvasRef.current.width,
          canvasRef.current.height
        )
        const blob = await new Promise(resolve => {
          canvasRef.current.toBlob(resolve, 'image/jpeg', 0.8)
        })
        clearTimeout(safetyClearTimeout)
        if (blob) {
          setCaptureCounter(prev => prev + 1)
          capturingInProgress.current = false
          return blob
        }
      } catch (err) {
        console.error('Error in standard capture mode:', err)
      }
      clearTimeout(safetyClearTimeout)
      capturingInProgress.current = false
      return null
    } catch (error) {
      console.error('Overall capture error:', error)
      capturingInProgress.current = false
      return null
    }
  }

  const uploadProctorImage = async imageBlob => {
    if (!imageBlob) return

    try {
      const assessmentAssignedId =
        currentAssessment?.assessment_assigned_id ||
        assessment?.assessment_assigned_id

      // OTP/email-invite candidates have no portal student id — fall back to the
      // invite assigned id, then the assessmentAssignedId (mirrors RoleBased /
      // Communication). The server only needs assessmentAssignedId.
      const otpAssignedId =
        (typeof sessionStorage !== 'undefined' &&
          sessionStorage.getItem('assessment_invite_assigned_id')) || null
      const resolvedStudentId =
        studentData?.id ||
        studentData?.studentId ||
        currentAssessment?.studentId ||
        studentId ||
        otpAssignedId ||
        assessmentAssignedId

      if (!assessmentAssignedId) {
        console.error('❌ [PROCTORING] No assessment_assigned_id found for proctoring upload')
        return
      }

      console.log('🔍 [PROCTORING] Uploading with IDs:', {
        assessmentAssignedId,
        resolvedStudentId
      })

      const base64Image = await blobToBase64(imageBlob)
      const formData = new FormData()
      const byteCharacters = atob(base64Image)
      const byteNumbers = new Array(byteCharacters.length)
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i)
      }
      const byteArray = new Uint8Array(byteNumbers)
      const imageFile = new Blob([byteArray], { type: 'image/jpeg' })

      formData.append('assessmentAssignedId', assessmentAssignedId)
      formData.append('studentId', resolvedStudentId)
      formData.append('image', imageFile)

      await studentRequest.post(
        'students/assessments/proctoring/uploadImage',
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      )

      console.log('✅ [PROCTORING] Image uploaded successfully')
    } catch (error) {
      console.error('❌ [PROCTORING] Error processing proctoring image:', error)
    }
  }

  const setupProctoringInterval = () => {
    if (captureInterval.current) {
      clearInterval(captureInterval.current)
    }
    captureInterval.current = setInterval(async () => {
      if (!proctoringActive.current || capturingInProgress.current) return
      try {
        const imageBlob = await captureImage()
        if (imageBlob) {
          await uploadProctorImage(imageBlob)
        }
      } catch (error) {
        console.error('Error in proctoring interval:', error)
      }
    }, 13000)
  }
  // Initialize global timer once - sum of all section times
  useEffect(() => {
    if (sections.length > 0 && !globalTimerInitialized.current) {
      const totalSeconds = sections.reduce((sum, s) => sum + (s.section_time * 60), 0)
      setTimeLeft(totalSeconds)
      globalTimerInitialized.current = true
    }
  }, [sections])

  // Date-based single global timer: pin endTime once and compute remaining each tick.
  const endTimeRef = useRef(null)
  const activeRef = useRef(isAssessmentActive)
  const handleSubmitRefTimer = useRef(handleSubmit)
  const submittedRef = useRef(false)
  useEffect(() => { activeRef.current = isAssessmentActive }, [isAssessmentActive])
  useEffect(() => { handleSubmitRefTimer.current = handleSubmit }, [handleSubmit])

  useEffect(() => {
    if (!(timeLeft > 0 && isAssessmentActive)) return
    if (endTimeRef.current === null) {
      endTimeRef.current = Date.now() + timeLeft * 1000
    }
  }, [timeLeft, isAssessmentActive])

  useEffect(() => {
    timerRef.current = setInterval(() => {
      if (!endTimeRef.current || !activeRef.current) return
      const remaining = Math.max(0, Math.ceil((endTimeRef.current - Date.now()) / 1000))
      if (remaining <= 0) {
        if (!submittedRef.current) {
          submittedRef.current = true
          setTimeLeft(0)
          handleSubmitRefTimer.current && handleSubmitRefTimer.current()
        }
        return
      }
      setTimeLeft(remaining)
    }, 1000)
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    }
  }, [])
  useEffect(() => {
    assessmentActiveRef.current = isAssessmentActive
    completedRef.current = showCompletion
    handleSubmitRef.current = handleSubmit
  }, [isAssessmentActive, showCompletion])

  // Prevent accidental exit during active assessment (reload, back button, tab close)
  useEffect(() => {
    if (!isAssessmentActive || showCompletion) return;

    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };

    const handlePopState = () => {
      window.history.pushState(null, '', window.location.href);
    };

    window.history.pushState(null, '', window.location.href);
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isAssessmentActive, showCompletion])

  // Track fullscreen (only when fullscreen lockdown is enforced)
  useEffect(() => {
    if (!enforceFullscreen) return

    const handleFullscreenChange = async () => {
      const isCurrentlyFullscreen = Boolean(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      )

      setIsFullscreen(isCurrentlyFullscreen)
      setIsAssessmentFullscreen(isCurrentlyFullscreen)

      if (!isCurrentlyFullscreen && isMobileInterruptionFullscreenExit()) return

      // Always treat any exit from fullscreen as a violation
      if (isAssessmentActive && !isCurrentlyFullscreen && !showCompletion) {
        setTabSwitchCount(prev => {
          const newCount = prev + 1
          if (newCount >= MAX_TAB_SWITCH_WARNINGS) {
            setShowViolationModal(true)
            setViolationType('fullscreen-exit')
            setIsAssessmentActive(false)
            trackAssessmentAutoSubmitted(
              currentAssessment?.assessment_assigned_id || assessment?.assessment_assigned_id,
              newCount,
              'Max violation count reached',
              'Custom Assessment',
              currentAssessment?.isCorporate || assessment?.isCorporate,
              currentAssessment?.entityName || assessment?.entityName
            )
            // Add a delay before auto-submitting
            isAutoSubmittedRef.current = true
            violationSubmitTimerRef.current = setTimeout(() => {
              submitAfterViolation()
            }, 5000)
          } else {
            setShowReentryModal(true)
            setIsAssessmentActive(false)
            trackFullscreenExited(
              currentAssessment?.assessment_assigned_id || assessment?.assessment_assigned_id,
              'Custom Assessment',
              newCount,
              currentAssessment?.isCorporate || assessment?.isCorporate,
              currentAssessment?.entityName || assessment?.entityName
            )
          }
          return newCount
        })
      }
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange)
    document.addEventListener('mozfullscreenchange', handleFullscreenChange)
    document.addEventListener('MSFullscreenChange', handleFullscreenChange)

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange)
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange)
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange)
    }
  }, [enforceFullscreen, isAssessmentActive, showCompletion, handleSubmit, assessment, currentAssessment])

  // Track tab switching (only when proctoring is enabled)
  useEffect(() => {
    if (!proctoringEnabled) return

    const handleVisibilityChange = () => {
      if (
        document.visibilityState === 'hidden' &&
        assessmentActiveRef.current &&
        !completedRef.current
      ) {
        if (markMobileInterruption()) return
        setTabSwitchCount(prev => {
          const newCount = prev + 1
          if (newCount >= MAX_TAB_SWITCH_WARNINGS) {
            setShowViolationModal(true)
            setViolationType('tab-switch')
            setIsAssessmentActive(false)
            trackViolationDetected(
              currentAssessment?.assessment_assigned_id || assessment?.assessment_assigned_id,
              'Custom Assessment',
              newCount,
              'tab-switch',
              currentAssessment?.isCorporate || assessment?.isCorporate,
              currentAssessment?.entityName || assessment?.entityName
            )
            trackAssessmentAutoSubmitted(
              currentAssessment?.assessment_assigned_id || assessment?.assessment_assigned_id,
              newCount,
              'Max violation count reached',
              'Custom Assessment',
              currentAssessment?.isCorporate || assessment?.isCorporate,
              currentAssessment?.entityName || assessment?.entityName
            )
            // Add a delay before auto-submitting
            isAutoSubmittedRef.current = true
            violationSubmitTimerRef.current = setTimeout(() => {
              submitAfterViolation()
            }, 5000)
          } else {
            setShowReentryModal(true)
            setIsAssessmentActive(false)
            trackViolationDetected(
              currentAssessment?.assessment_assigned_id || assessment?.assessment_assigned_id,
              'Custom Assessment',
              newCount,
              'tab-switch',
              currentAssessment?.isCorporate || assessment?.isCorporate,
              currentAssessment?.entityName || assessment?.entityName
            )
          }
          return newCount
        })
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [proctoringEnabled, assessment, currentAssessment])

  // Add fullscreen body class management (only when proctoring is enabled)
  useEffect(() => {
    if (!proctoringEnabled) return

    if (isAssessmentFullscreen) {
      document.body.classList.add('assessment-fullscreen-mode')
      const navbar = document.querySelector(
        'nav, .navbar, .header, .app-header, .top-nav'
      )
      if (navbar) {
        navbar.style.display = 'none'
      }
    } else {
      document.body.classList.remove('assessment-fullscreen-mode')
      const navbar = document.querySelector(
        'nav, .navbar, .header, .app-header, .top-nav'
      )
      if (navbar) {
        navbar.style.display = ''
      }
    }

    return () => {
      document.body.classList.remove('assessment-fullscreen-mode')
      const navbar = document.querySelector(
        'nav, .navbar, .header, .app-header, .top-nav'
      )
      if (navbar) {
        navbar.style.display = ''
      }
    }
  }, [proctoringEnabled, isAssessmentFullscreen])
  useEffect(() => {
    if (!isAssessmentActive || showCompletion) {
      proctoringActive.current = false
      return
    }

    const assessmentAssignedId =
      currentAssessment?.assessment_assigned_id ||
      assessment?.assessment_assigned_id

    const resolvedStudentId =
      studentData?.id ||
      studentData?.studentId ||
      currentAssessment?.studentId ||
      studentId

    // ponytail: studentId is optional for OTP-invite candidates (no portal login,
    // no redux studentData). The server resolves identity from assessmentAssignedId
    // -> primaryEmail, so only assessmentAssignedId is required to start proctoring.
    // Requiring it here skipped the whole snapshot interval for invite candidates,
    // leaving a single camera-guard frame as the only proctoring image.
    if (!assessmentAssignedId) {
      console.log('🔍 [PROCTORING] Missing assessmentAssignedId, skipping proctoring setup:', {
        assessmentAssignedId
      })
      proctoringActive.current = false
      return
    }

    console.log('🔍 [PROCTORING] Starting proctoring with IDs:', {
      assessmentAssignedId,
      resolvedStudentId: resolvedStudentId || '(invite candidate — none)'
    })

    proctoringActive.current = true
    setupProctoringInterval()

      // Take initial capture immediately when assessment starts
      ; (async () => {
        try {
          console.log('🔍 [PROCTORING] Taking initial capture')
          const imageBlob = await captureImage()
          if (imageBlob) {
            await uploadProctorImage(imageBlob)
            console.log('✅ [PROCTORING] Initial capture uploaded successfully')
          }
        } catch (error) {
          console.error('❌ [PROCTORING] Error in initial proctoring capture:', error)
        }
      })()

    return () => {
      proctoringActive.current = false
      if (captureInterval.current) {
        clearInterval(captureInterval.current)
        captureInterval.current = null
      }
      if (captureRef.current && captureRef.current.srcObject) {
        captureRef.current.srcObject.getTracks().forEach(track => {
          if (track.readyState === 'live') track.stop()
        })
        captureRef.current.srcObject = null
      }
    }
  }, [
    isAssessmentActive,
    showCompletion,
    currentAssessment?.assessment_assigned_id,
    studentData?.id,
    assessment?.assessment_assigned_id,
    studentId
  ])

  // Cleanup proctoring on unmount
  useEffect(() => {
    return () => {
      if (captureInterval.current) {
        clearInterval(captureInterval.current)
        captureInterval.current = null
      }
      if (window.proctorStreamTracks) {
        window.proctorStreamTracks.forEach(track => {
          if (track.readyState === 'live') track.stop()
        })
        window.proctorStreamTracks = []
      }
      if (captureRef.current) {
        captureRef.current.srcObject = null
        captureRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    // Load saved response for current question if it exists
    if (currentQuestion?.id && responses[currentQuestion.id] !== undefined) {
      // Find the index of the option with matching ID
      const savedOptionId = responses[currentQuestion.id]
      const optionIndex = currentQuestion.options.findIndex(opt => opt.id === savedOptionId)
      setSelectedAnswer(optionIndex !== -1 ? optionIndex : null)
    } else {
      setSelectedAnswer(null)
    }
  }, [currentQuestionIndex, currentSectionIndex, currentQuestion?.id, responses])
  const isUAT = (process.env.ENVIRONMENT || '').toUpperCase() === 'UAT';

  // Prevent right-click
  useEffect(() => {
    if (isUAT) return;
    const handleContextMenu = (e) => {
      e.preventDefault()
      trackRightClick({
        assessmentId: assessment?.id
      })
    }

    document.addEventListener('contextmenu', handleContextMenu)
    return () => document.removeEventListener('contextmenu', handleContextMenu)
  }, [])

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
  }

  const handleSectionTimeout = () => {
    if (currentSectionIndex < sections.length - 1) {
      setCurrentSectionIndex(prev => prev + 1)
      setCurrentQuestionIndex(0)
      setSelectedAnswer(null)
    } else {
      handleSubmit()
    }
  }

  const handleAnswerSelect = (optionIndex) => {
    setSelectedAnswer(optionIndex)

    const timeTaken = Math.floor((Date.now() - questionStartTimeRef.current) / 1000)

    // Get the selected option's ID from the options array
    const selectedOptionId = currentQuestion.options[optionIndex]?.id

    // Update local responses state with option ID
    setResponses(prev => ({
      ...prev,
      [currentQuestion.id]: selectedOptionId  // Store the option ID (UUID)
    }))

    // Dispatch the save action with proper parameters
    dispatch(saveQuestionResponse(
      currentQuestion.id,           // questionId
      selectedOptionId,             // answer (the option ID)
      currentAssessment?.assessment_assigned_id || assessment?.assessment_assigned_id,  // assessment_assigned_id
      null,                         // subQuestionId (not needed for custom assessment)
      null                          // questionType (not needed for custom assessment)
    ))

    trackQuestionAnswered({
      assessmentId: assessment?.id,
      questionId: currentQuestion.id,
      questionNumber: currentQuestionIndex + 1,
      sectionName: currentSection.section_name
    })
  }
  const handleNext = () => {
    if (currentQuestionIndex < currentSection.questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1)
      setSelectedAnswer(null)
      questionStartTimeRef.current = Date.now()

    } else if (currentSectionIndex < sections.length - 1) {
      setCurrentSectionIndex(prev => prev + 1)
      setCurrentQuestionIndex(0)
      setSelectedAnswer(null)
      questionStartTimeRef.current = Date.now()
    }
  }

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1)
      setSelectedAnswer(null)
      questionStartTimeRef.current = Date.now()
    } else if (currentSectionIndex > 0) {
      const prevSectionIndex = currentSectionIndex - 1
      setCurrentSectionIndex(prevSectionIndex)
      setCurrentQuestionIndex(sections[prevSectionIndex].questions.length - 1)
      setSelectedAnswer(null)
      questionStartTimeRef.current = Date.now()
    }
  }

  const stopProctoringCamera = () => {
    proctoringActive.current = false
    if (captureInterval.current) {
      clearInterval(captureInterval.current)
      captureInterval.current = null
    }
    if (captureRef.current && captureRef.current.srcObject) {
      try {
        captureRef.current.srcObject.getTracks().forEach(track => track.stop())
      } catch (e) { /* noop */ }
      captureRef.current.srcObject = null
    }
    captureRef.current = null
    if (window.proctorStreamTracks) {
      window.proctorStreamTracks.forEach(track => {
        try { track.stop() } catch (e) { /* noop */ }
      })
      window.proctorStreamTracks = []
    }
  }

  const handleSubmit = async () => {
    try {
      console.log('🔍 [Custom Assessment] Submitting with responses:', responses)

      // Stop camera/proctoring stream on submit
      stopProctoringCamera()

      // Calculate total time taken: total allotted - remaining
      const totalAllotted = sections.reduce((sum, s) => sum + (s.section_time * 60), 0)
      const totalTakenTime = totalAllotted - timeLeft
      // Store final total for completion screen
      totalTimeTakenRef.current = totalTakenTime
      setTotalTimeTakenDisplay(totalTakenTime)

      const result = await dispatch(submitAssessment({
        assessment_assigned_id: currentAssessment?.assessment_assigned_id || assessment?.assessment_assigned_id,
        response: responses,
        full_name: currentAssessment?.full_name || studentId,
        totalTakenTime: totalTakenTime,
        isAutoSubmitted: isAutoSubmittedRef.current,
      }))

      console.log('✅ [Custom Assessment] Submission result:', result)

      trackAssessmentCompleted(
        totalTakenTime,
        currentAssessment?.assessment_assigned_id || assessment?.assessment_assigned_id,
        'Custom Assessment',
        currentAssessment?.isCorporate || assessment?.isCorporate,
        currentAssessment?.entityName || assessment?.entityName
      )

      // ✅ ADDED: Exit fullscreen before showing completion
      await exitFullscreen()

      setShowCompletion(true)
    } catch (error) {
      console.error('❌ [Custom Assessment] Error submitting assessment:', error)
      alert('Failed to submit assessment. Please try again.')
    }
  }
  const handleAutoSubmit = (reason) => {
    setViolationType(reason)
    setShowViolationModal(true)
    trackAssessmentAutoSubmitted({
      assessmentId: assessment?.id,
      assessmentType: 'Custom Assessment',
      reason
    })
    isAutoSubmittedRef.current = true
    setTimeout(() => {
      handleSubmit()
    }, 3000)
  }

  const requestFullscreenAccess = async () => {
    try {
      const element = document.documentElement

      // Add body class to hide navbar BEFORE fullscreen request
      document.body.classList.add('assessment-fullscreen-mode')
      const navbar = document.querySelector(
        'nav, .navbar, .header, .app-header, .top-nav'
      )
      if (navbar) {
        navbar.style.display = 'none'
      }

      if (element.requestFullscreen) {
        await element.requestFullscreen()
      } else if (element.mozRequestFullScreen) {
        await element.mozRequestFullScreen()
      } else if (element.webkitRequestFullscreen) {
        await element.webkitRequestFullscreen()
      } else if (element.msRequestFullscreen) {
        await element.msRequestFullscreen()
      }

      // Wait a bit to ensure fullscreen is active
      setTimeout(() => {
        setIsAssessmentFullscreen(true)
        setShowFullscreenModal(false)
        setIsAssessmentActive(true)
      }, 500)

      trackFullscreenEntered(
        currentAssessment?.assessment_assigned_id || assessment?.assessment_assigned_id,
        'Custom Assessment',
        false,
        currentAssessment?.isCorporate || assessment?.isCorporate,
        currentAssessment?.entityName || assessment?.entityName
      )
    } catch (error) {
      console.error('Error entering fullscreen:', error)
      // Still proceed with assessment even if fullscreen fails
      setIsAssessmentFullscreen(true)
      setShowFullscreenModal(false)
      setIsAssessmentActive(true)
    }
  }

  // Lite-mode (iPhone) re-entry: there is no fullscreen to return to, so just
  // dismiss the warning and resume. The tab-switch/app-switch was still counted.
  const handleReturnLite = () => {
    setShowReentryModal(false)
    setIsAssessmentActive(true)
  }

  const handleReturnToFullscreen = async () => {
    setShowReentryModal(false)

    try {
      const element = document.documentElement

      // Add body class to hide navbar
      document.body.classList.add('assessment-fullscreen-mode')
      const navbar = document.querySelector(
        'nav, .navbar, .header, .app-header, .top-nav'
      )
      if (navbar) {
        navbar.style.display = 'none'
      }

      if (element.requestFullscreen) {
        await element.requestFullscreen()
      } else if (element.mozRequestFullScreen) {
        await element.mozRequestFullScreen()
      } else if (element.webkitRequestFullscreen) {
        await element.webkitRequestFullscreen()
      } else if (element.msRequestFullscreen) {
        await element.msRequestFullscreen()
      }

      trackFullscreenEntered(
        currentAssessment?.assessment_assigned_id || assessment?.assessment_assigned_id,
        'Custom Assessment',
        true,
        currentAssessment?.isCorporate || assessment?.isCorporate,
        currentAssessment?.entityName || assessment?.entityName
      )
      setIsAssessmentFullscreen(true)
      setIsAssessmentActive(true)
    } catch (error) {
      console.error('Error re-entering fullscreen:', error)
      setIsAssessmentFullscreen(true)
      setIsAssessmentActive(true)
    }
  }

  const exitFullscreen = async () => {
    try {
      // Remove body class to show navbar
      document.body.classList.remove('assessment-fullscreen-mode')
      const navbar = document.querySelector(
        'nav, .navbar, .header, .app-header, .top-nav'
      )
      if (navbar) {
        navbar.style.display = ''
      }

      if (document.exitFullscreen) {
        await document.exitFullscreen()
      } else if (document.mozCancelFullScreen) {
        await document.mozCancelFullScreen()
      } else if (document.webkitExitFullscreen) {
        await document.webkitExitFullscreen()
      } else if (document.msExitFullscreen) {
        await document.msExitFullscreen()
      }

      setIsAssessmentFullscreen(false)
    } catch (error) {
      console.error('Error exiting fullscreen:', error)
      document.body.classList.remove('assessment-fullscreen-mode')
      const navbar = document.querySelector(
        'nav, .navbar, .header, .app-header, .top-nav'
      )
      if (navbar) {
        navbar.style.display = ''
      }
      setIsAssessmentFullscreen(false)
    }
  }
  const handleQuestionHover = (globalIndex, event) => {
    if (sidebarExpanded) return

    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
    }

    hoverTimeoutRef.current = setTimeout(() => {
      const rect = event.currentTarget.getBoundingClientRect()
      setPopoverPosition({ top: rect.top })
      setHoveredQuestion(globalIndex)
    }, 300)
  }

  const handleQuestionLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
    }
    setTimeout(() => {
      setHoveredQuestion(null)
    }, 100)
  }

  const handleQuestionSelect = (sectionIdx, questionIdx) => {
    setCurrentSectionIndex(sectionIdx)
    setCurrentQuestionIndex(questionIdx)
    setSelectedAnswer(null)
    questionStartTimeRef.current = Date.now()
  }

  if (showCompletion) {
    return <Completion assessment={assessment} totalTimeTaken={totalTimeTakenDisplay || totalTimeTakenRef.current} />
  }

  // Show fullscreen modal first, BEFORE checking if assessment is active (only when fullscreen is enforced)
  if (enforceFullscreen && showFullscreenModal) {
    return <FullscreenEntryModal onAccept={requestFullscreenAccess} />
  }

  if (proctoringEnabled && showReentryModal) {
    return (
      <FullscreenReentryModal
        onReturn={enforceFullscreen ? handleReturnToFullscreen : handleReturnLite}
        warningCount={tabSwitchCount}
        lite={!enforceFullscreen}
      />
    )
  }

  if (proctoringEnabled && showViolationModal) {
    return <ViolationModal onSubmit={submitAfterViolation} />
  }

  // Only gate on isAssessmentActive when proctoring is enabled
  if (proctoringEnabled && !isAssessmentActive) {
    return null
  }

  if (!currentSection || !currentQuestion) {
    return <div>Loading...</div>
  }

  return (
    <>
      <MarkdownGlobalStyle />
      <FullscreenAssessmentContainer className="exam-shell" isFullscreen={isFullscreen}>
        {/* Sidebar Navigation */}
        <SidebarNav isExpanded={sidebarExpanded}>
          <SidebarHeader isExpanded={sidebarExpanded}>
            {sidebarExpanded && (
              <SidebarTitle isExpanded={sidebarExpanded}>
                Questions ({allQuestions.length})
              </SidebarTitle>
            )}
            <ToggleButton onClick={() => setSidebarExpanded(!sidebarExpanded)}>
              {sidebarExpanded ? '✕' : '▶'}
            </ToggleButton>
          </SidebarHeader>

          <SidebarContent isExpanded={sidebarExpanded}>
            <QuestionList>
              {sections.map((section, sectionIdx) => (
                <React.Fragment key={sectionIdx}>
                  {sidebarExpanded && (
                    <SectionTitle isExpanded={sidebarExpanded}>
                      {section.section_name}
                    </SectionTitle>
                  )}
                  {section.questions.map((q, qIdx) => {
                    const globalIdx = sections.slice(0, sectionIdx).reduce((sum, s) => sum + s.questions.length, 0) + qIdx
                    let status = 'unattempted'
                    if (sectionIdx === currentSectionIndex && qIdx === currentQuestionIndex) {
                      status = 'current'
                    } else if (responses[q.id] !== undefined) {  // ✅ Check if it exists, not if it's truthy
                      status = 'attempted'
                    }

                    return (
                      <QuestionItem
                        key={q.id}
                        ref={el => (questionItemRefs.current[globalIdx] = el)}
                        isExpanded={sidebarExpanded}
                        onClick={() => handleQuestionSelect(sectionIdx, qIdx)}
                        onMouseEnter={e => handleQuestionHover(globalIdx, e)}
                        onMouseLeave={handleQuestionLeave}
                      >
                        <QuestionNumber
                          isExpanded={sidebarExpanded}
                          status={status}
                        >
                          {qIdx + 1}
                        </QuestionNumber>
                        {sidebarExpanded && (
                          <QuestionText isExpanded={sidebarExpanded}>
                            <div className="markdown-body">
                              <ReactMarkdown
                                remarkPlugins={[remarkMath]}
                                rehypePlugins={[rehypeKatex]}
                                skipHtml={true}
                              >
                                {q.questionText}
                              </ReactMarkdown>
                            </div>
                          </QuestionText>
                        )}
                      </QuestionItem>
                    )
                  })}
                </React.Fragment>
              ))}
            </QuestionList>
          </SidebarContent>
        </SidebarNav>

        {/* Hover Popover */}
        {hoveredQuestion !== null && !sidebarExpanded && (
          <QuestionPopover
            show={true}
            top={popoverPosition.top}
          >
            <div className="popover-header">
              <span className="popover-question-number">
                Question {hoveredQuestion + 1}
              </span>
              <span
                className="popover-status"
                style={{
                  background: (() => {
                    const q = allQuestions[hoveredQuestion]
                    if (q.sectionIndex === currentSectionIndex && q.questionIndex === currentQuestionIndex) {
                      return '#3b82f6'
                    } else if (responses[q.id] !== undefined) {  // ✅ Check if it exists, not if it's truthy
                      return '#10b981'
                    }
                    return '#6b7280'
                  })()
                }}
              >
                {(() => {
                  const q = allQuestions[hoveredQuestion]
                  if (q.sectionIndex === currentSectionIndex && q.questionIndex === currentQuestionIndex) {
                    return 'Current'
                  } else if (responses[q.id] !== undefined) {  // ✅ Check if it exists, not if it's truthy
                    return 'Attempted'
                  }
                  return 'Unattempted'
                })()}
              </span>
            </div>
            <div className="popover-content">
              <ReactMarkdown
                remarkPlugins={[remarkMath]}
                rehypePlugins={[rehypeKatex]}
              >
                {allQuestions[hoveredQuestion]?.questionText}
              </ReactMarkdown>
            </div>
          </QuestionPopover>
        )}

        {/* Main Content */}
        <MainContent className="exam-scroll">
          <TopBar>
            <AssessmentTitle>
              <PluginLiveLogo width={120} />
              <span>{currentSection.section_name}</span>
            </AssessmentTitle>
            <TimerDisplay isWarning={timeLeft < 300}>
              ⏱ {formatTime(timeLeft)}
            </TimerDisplay>
          </TopBar>

          <QuestionArea className="exam-scroll">
            <QuestionContent>
              <QuestionHeader>
                <QuestionTitle>Question {currentQuestionIndex + 1}</QuestionTitle>
                <QuestionMeta>
                  <MetaBadge>{currentSection.section_name}</MetaBadge>
                  <MetaBadge>
                    {(currentSection.total_marks / currentSection.questions.length).toFixed(1)} marks
                  </MetaBadge>
                </QuestionMeta>
              </QuestionHeader>

              <QuestionTextArea>
                <ReactMarkdown
                  remarkPlugins={[remarkMath]}
                  rehypePlugins={[rehypeKatex]}
                >
                  {currentQuestion.questionText}
                </ReactMarkdown>
                {currentQuestion.question_image_url && (
                  <img src={currentQuestion.question_image_url} alt="Question" />
                )}
              </QuestionTextArea>

              <OptionsContainer>
                {currentQuestion.options?.map((option, idx) => (
                  <OptionItem
                    key={option.id}
                    isSelected={selectedAnswer === idx}
                    onClick={() => handleAnswerSelect(idx)}
                  >
                    <OptionText isSelected={selectedAnswer === idx}>
                      {String.fromCharCode(65 + idx)}. {option.optionText}
                    </OptionText>
                    {option.option_image_url && (
                      <img src={option.option_image_url} alt={`Option ${idx + 1}`} />
                    )}
                  </OptionItem>
                ))}
              </OptionsContainer>
            </QuestionContent>
          </QuestionArea>

          <BottomBar className="exam-actions">
            <NavButton
              onClick={handlePrevious}
              disabled={currentSectionIndex === 0 && currentQuestionIndex === 0}
            >
              ← Previous
            </NavButton>

            <div style={{ display: 'flex', gap: '12px' }}>
              {currentSectionIndex === sections.length - 1 &&
                currentQuestionIndex === currentSection.questions.length - 1 ? (
                <SubmitButton onClick={() => setShowSubmitModal(true)}>
                  Submit Assessment
                </SubmitButton>
              ) : (
                <NavButton primary onClick={handleNext}>
                  Next →
                </NavButton>
              )}
            </div>
          </BottomBar>
        </MainContent>

        {/* Submit Confirmation Modal */}
        {showSubmitModal && (
          <FullscreenModalOverlay>
            <FullscreenModalContent>
              <FullscreenTitle>Submit Assessment?</FullscreenTitle>
              <FullscreenDescription>
                Are you sure you want to submit? You cannot change your answers after submission.
              </FullscreenDescription>
              <div style={{ display: 'flex', gap: '12px', marginTop: '16px', justifyContent: 'center' }}>
                <NavButton onClick={() => setShowSubmitModal(false)}>Cancel</NavButton>
                <PrimaryButton onClick={handleSubmit}>Submit</PrimaryButton>
              </div>
            </FullscreenModalContent>
          </FullscreenModalOverlay>
        )}
      </FullscreenAssessmentContainer>
    </>
  )
}

export default CustomAssessment
